import { useCallback } from "react";
import { useLanguage } from "../provider/LanguageProvider";

// Warn once per missing key per session (dev only).
const warnedKeys = new Set();

// One PluralRules instance per language for the session.
const pluralRulesCache = new Map();
const pluralCategory = (language, count) => {
  try {
    if (!pluralRulesCache.has(language)) {
      pluralRulesCache.set(language, new Intl.PluralRules(language));
    }
    return pluralRulesCache.get(language).select(count);
  } catch {
    return count === 1 ? "one" : "other";
  }
};

/**
 * Custom hook for translations.
 * Returns:
 *  - t(key, fallback, options): translates a key; missing keys fall back to
 *    the explicit fallback, then the key itself. `options` interpolates
 *    {{placeholders}}. New code should NOT pass an inline English fallback —
 *    English lives in src/i18n/en/ only.
 *
 *    Plurals: when `options.count` is a number, the key resolves through
 *    CLDR plural categories via Intl.PluralRules — `key_one`, `key_other`,
 *    and for Arabic also `key_zero`, `key_two`, `key_few`, `key_many`.
 *    Missing categories fall back to `key_other`, then the bare key.
 *    `{{count}}` interpolates like any other placeholder:
 *        t('huddle.replies', undefined, { count: n })
 *        // en: replies_one "{{count}} reply" / replies_other "{{count}} replies"
 *
 *  - language: current language code (e.g. 'en', 'ar')
 *  - setLanguage: function to change language
 *  - dir: current direction ('ltr' or 'rtl')
 *  - languages: map of all supported languages, keyed by code
 *  - locales: raw locale registry rows from the API (code, direction, labels…)
 *  - refreshTranslations(lang): re-pull the DB delta, e.g. after an admin save
 */
const useTranslation = () => {
  const { translations, language, setLanguage, dir, languages, locales, refreshTranslations } =
    useLanguage();

  const t = useCallback(
    (key, fallback, options = {}) => {
      let resolvedKey = key;

      // Plural resolution: key_<category> -> key_other -> key.
      if (typeof options?.count === "number") {
        const category = pluralCategory(language, options.count);
        if (translations[`${key}_${category}`] !== undefined) {
          resolvedKey = `${key}_${category}`;
        } else if (translations[`${key}_other`] !== undefined) {
          resolvedKey = `${key}_other`;
        }
      }

      if (
        translations[resolvedKey] === undefined &&
        process.env.NODE_ENV === "development" &&
        !warnedKeys.has(resolvedKey)
      ) {
        warnedKeys.add(resolvedKey);
        console.warn(`[i18n] missing key: ${resolvedKey}`);
      }

      let translation = translations[resolvedKey] ?? fallback ?? key;

      // Handle interpolation if options are provided
      if (options && typeof options === "object") {
        Object.keys(options).forEach((optKey) => {
          // split/join instead of RegExp: placeholder names never need escaping
          translation = translation
            .split(`{{${optKey}}}`)
            .join(String(options[optKey]));
        });
      }

      return translation;
    },
    [translations, language]
  );

  return { t, language, setLanguage, dir, languages, locales, refreshTranslations };
};

export default useTranslation;
